import { useEffect, useState } from 'react';
import type { components } from '@bookingdei/api-client/types';
import { api } from '../../api';
import { useLanguage } from '../../shell/language-context';
import { HomeListingCard } from './home-listing-card';
import { placeSponsored } from './place-sponsored';
import type { PlaceOption } from './hero-location-panel';
import type { HomeFormat } from './use-home-format';

type BusinessSummary = components['schemas']['BusinessSummary'];
type Category = components['schemas']['Category'];
type SearchResults = components['schemas']['SearchResults'];

// Search sorts the browse bar offers. Nearest-first is deferred (DR-44), so the
// design's "Nearest" option is the server's default relevance order.
type Sort = 'relevance' | 'rating' | 'price';
const PAGE = 12;
const MORE = 8;

// "Popular near you." (design DC:586-642): the organic grid with the one sponsored
// placement at slot 3, and "All listings ›", which opens a browse bar (category,
// location, sort, verified only, result count) over live search with Load more.
export function PopularSection({
  nearby,
  categories,
  cities,
  fmt,
}: {
  nearby: BusinessSummary[];
  categories: Category[];
  cities: PlaceOption[];
  fmt: HomeFormat;
}) {
  const { t, lang } = useLanguage();
  const [open, setOpen] = useState(false);
  const [cat, setCat] = useState('');
  const [loc, setLoc] = useState('');
  const [sort, setSort] = useState<Sort>('relevance');
  const [verified, setVerified] = useState(false);
  const [items, setItems] = useState<BusinessSummary[]>([]);
  const [total, setTotal] = useState<number | null>(null);
  const [cursor, setCursor] = useState<string | undefined>();
  const [loading, setLoading] = useState(false);

  // Browse results: re-run whenever a filter changes while the bar is open.
  useEffect(() => {
    if (!open) return;
    const controller = new AbortController();
    setLoading(true);
    api
      .GET('/search', {
        params: { query: { ...(cat ? { category: cat } : {}), ...(loc ? { location: loc } : {}), sort, limit: PAGE } },
        signal: controller.signal,
        lang,
      })
      .then((r: SearchResults) => {
        setItems(r.items ?? []);
        setTotal(r.total_estimate ?? r.items?.length ?? 0);
        setCursor(r.next_cursor);
        setLoading(false);
      })
      .catch(() => setLoading(false));
    return () => controller.abort();
  }, [open, cat, loc, sort, lang]);

  // The closed-state context line counts what search would return unfiltered.
  const [placesTotal, setPlacesTotal] = useState<number | null>(null);
  useEffect(() => {
    const controller = new AbortController();
    api
      .GET('/search', { params: { query: { limit: 1 } }, signal: controller.signal, lang })
      .then((r: SearchResults) => setPlacesTotal(r.total_estimate ?? null))
      .catch(() => undefined);
    return () => controller.abort();
  }, [lang]);

  const loadMore = () => {
    if (!cursor) return;
    setLoading(true);
    api
      .GET('/search', {
        params: { query: { ...(cat ? { category: cat } : {}), ...(loc ? { location: loc } : {}), sort, limit: MORE, cursor } },
        lang,
      })
      .then((r: SearchResults) => {
        setItems((cur) => [...cur, ...(r.items ?? [])]);
        setCursor(r.next_cursor);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  };

  // "Verified only" has no search parameter, so it narrows the loaded results.
  const browse = verified ? items.filter((b) => b.verified) : items;
  const cards = open ? placeSponsored(browse, 2) : placeSponsored(nearby, 2, 7);
  const matchCount = verified ? browse.filter((b) => !b.sponsored).length : (total ?? 0);
  if (!open && cards.length === 0) return null;

  return (
    <section className="hm-sec hm-sec--popular" data-reveal="out">
      <div className="hm-head hm-head--mb">
        <div>
          <h2 className="hm-h2">{t('dc50.popular.title')}</h2>
          <div className="hm-sub">
            {open
              ? t('dc50.popular.match_count', { n: fmt.num(matchCount) })
              : placesTotal !== null
                ? t('dc50.popular.places_in', { n: fmt.num(placesTotal) })
                : ' '}
          </div>
        </div>
        <button type="button" className="hm-link hm-link--btn" aria-expanded={open} onClick={() => setOpen((v) => !v)}>
          {open ? t('dc50.popular.collapse') : t('dc50.popular.all_listings')}
        </button>
      </div>
      {open ? (
        <div className="hm-browse">
          <select className="hm-browse__select" value={cat} aria-label={t('dc50.popular.category_label')} onChange={(e) => setCat(e.target.value)}>
            <option value="">{t('dc50.popular.all_categories')}</option>
            {categories.map((c) => (
              <option key={c.id} value={c.id}>
                {fmt.catLabel(c.id)}
              </option>
            ))}
          </select>
          <select className="hm-browse__select" value={loc} aria-label={t('dc50.popular.location_label')} onChange={(e) => setLoc(e.target.value)}>
            <option value="">{t('dc50.popular.all_locations')}</option>
            {cities.map((c) => (
              <option key={c.id} value={c.slug}>
                {c.label}
              </option>
            ))}
          </select>
          <select className="hm-browse__select" value={sort} aria-label={t('dc50.popular.sort_label')} onChange={(e) => setSort(e.target.value as Sort)}>
            <option value="relevance">{t('dc50.popular.sort_relevance')}</option>
            <option value="rating">{t('dc50.popular.sort_rating')}</option>
            <option value="price">{t('dc50.popular.sort_price')}</option>
          </select>
          <button
            type="button"
            className={verified ? 'hm-browse__ver is-on' : 'hm-browse__ver'}
            aria-pressed={verified}
            onClick={() => setVerified((v) => !v)}
          >
            ✓ {t('dc50.popular.verified_only')}
          </button>
          <span className="hm-browse__count" aria-live="polite">
            {t(matchCount === 1 ? 'dc50.popular.result_one' : 'dc50.popular.results', { n: fmt.num(matchCount) })}
          </span>
        </div>
      ) : null}
      <div className="hm-grid" aria-busy={loading}>
        {cards.map((b) => (
          <HomeListingCard key={b.id} business={b} fmt={fmt} photo="background" />
        ))}
      </div>
      <div className="hm-note hm-note--grid">{t('dc50.popular.rankings_note')}</div>
      {open && !loading && browse.length === 0 ? <div className="hm-popular__empty">{t('dc50.popular.empty')}</div> : null}
      {open && browse.length > 0 && cursor ? (
        <div className="hm-popular__more">
          <button type="button" className="hm-popular__more-btn" disabled={loading} onClick={loadMore}>
            {t('dc50.popular.load_more')}
          </button>
        </div>
      ) : null}
    </section>
  );
}
